import { useState, useEffect } from 'react';
import venuesAPI, { Venue, VenueFilters } from '../services/venues.api';

interface UseVenuesResult {
  venues: Venue[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Custom hook to fetch and manage venues
 * @param filters - Optional filters for venues
 * @param activeOnly - If true, only fetch active venues (default: false)
 */
export const useVenues = (
  filters?: VenueFilters,
  activeOnly: boolean = false
): UseVenuesResult => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchVenues = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('useVenues: Fetching venues...', { filters, activeOnly });

      let data: Venue[];
      
      if (activeOnly) {
        data = await venuesAPI.getActiveVenues();
      } else {
        data = await venuesAPI.getVenues(filters);
      }

      console.log('useVenues: Received', data.length, 'venues');
      setVenues(data);
    } catch (err) {
      console.error('useVenues: Error', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch venues'));
      setVenues([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly, filters?.type, filters?.status, filters?.search]);

  return {
    venues,
    loading,
    error,
    refetch: fetchVenues,
  };
};

/**
 * Custom hook to fetch active venues (convenience wrapper)
 */
export const useActiveVenues = (): UseVenuesResult => {
  return useVenues(undefined, true);
};

export default useVenues;